import React, { useMemo } from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { TranslateFunction } from 'react-localize-redux';

import { List, PriceTag, RadioButton } from '../../../../../components/atoms';
import { ListItem } from '../../../../../components/atoms/List';
import { RadioGroup } from '../../../../../components/layouts';
import { Currency } from '../../../../../core/api/general/general.types';
import { SavedRestaurantVoucher } from '../../../../../core/api/payment/payment.types';
import { formatNumberToCurrency } from '../../../../../utils/basketUtils';
import { restaurantVoucherUtils } from '../utils';

type Props = {
  currency: Currency;
  currentLang: string;
  isVoucherSectionDisabled: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  orderTotalToPay: number;
  selectedVoucherId: number;
  translate: TranslateFunction;
  voucherOptions: SavedRestaurantVoucher[];
};

const useStyles = makeStyles((theme: Theme) => ({
  voucherOptions: {
    '&.fd-list': {
      paddingBottom: 0,
    },
    '& .fd-list__item': {
      padding: 0,
    },
  },

  voucherOption: {
    padding: theme.spacing(1.75, 0),
  },

  voucherOptionContainer: {
    alignItems: 'center',
    display: 'flex',
    flexFlow: 'row nowrap',
    marginLeft: theme.spacing(0.5),
  },

  voucherLogo: {
    justifyContent: 'flex-start',
    marginRight: theme.spacing(2),
  },

  voucherAvailableBalance: {
    marginLeft: 'auto',

    '#elixir #flipdish-budapest &.fd-price .fd-text, .fd-text': {
      color: theme.palette.grey[600],
    },
  },

  voucherCharge: {
    minWidth: '96px',
  },
}));

export function VoucherOptions(props: Props): JSX.Element {
  const classes = useStyles();
  const {
    currency,
    currentLang,
    isVoucherSectionDisabled,
    orderTotalToPay,
    onChange,
    selectedVoucherId,
    translate,
    voucherOptions,
  } = props;

  const getLocalisedTotal: (numberTotal: number) => string = (
    numberTotal: number,
  ) => formatNumberToCurrency(numberTotal, currency, currentLang);

  const renderVoucherOption = (
    voucher: SavedRestaurantVoucher,
  ): JSX.Element => {
    const {
      AvailableBalance: availableBalance,
      Description: description,
      PaymentAccountId: voucherId,
      VoucherBrand: voucherBrand,
      VoucherScheme: voucherScheme,
    } = voucher;

    const hasSufficientFunds =
      restaurantVoucherUtils.checkVoucherCoversFullAmount(
        availableBalance,
        orderTotalToPay,
      );
    const isVoucherDisabled = !hasSufficientFunds || isVoucherSectionDisabled;

    const voucherLogo = restaurantVoucherUtils.getVoucherLogo(
      description,
      isVoucherDisabled,
      'small',
      voucherBrand,
      voucherScheme,
    );

    const isVoucherSelected = voucherId === selectedVoucherId;
    const voucherAvailableBalance = availableBalance
      ? `(${getLocalisedTotal(availableBalance)})`
      : `(${translate('RestaurantVoucher_unknown_errorLabel')})`;

    const voucherCharge = isVoucherSelected
      ? getLocalisedTotal(
          restaurantVoucherUtils.getVoucherCharge(
            availableBalance,
            orderTotalToPay,
          ),
        )
      : null;

    const optionLabel = (
      <div className={classes.voucherOptionContainer}>
        <div className={classes.voucherLogo}>{voucherLogo}</div>

        <PriceTag
          className={classes.voucherAvailableBalance}
          localisedPrice={voucherAvailableBalance}
          translate={translate}
        />

        <PriceTag
          className={classes.voucherCharge}
          horizontalAlign="right"
          localisedPrice={voucherCharge}
          translate={translate}
        />
      </div>
    );

    return (
      <RadioButton
        checked={isVoucherSelected}
        className={classes.voucherOption}
        disabled={isVoucherDisabled}
        key={`voucher-option-${voucherId}`}
        label={optionLabel}
        testId={`${voucherScheme}-${voucherId}-option`}
        value={voucherId}
      />
    );
  };

  const renderVoucherOptions = useMemo(() => {
    const optionsMapped: ListItem[] = voucherOptions.map((option) => ({
      key: option.PaymentAccountId.toString(),
      primaryContent: renderVoucherOption(option),
    }));

    return optionsMapped;
  }, [
    isVoucherSectionDisabled,
    orderTotalToPay,
    selectedVoucherId,
    voucherOptions.length,
  ]);

  return (
    <RadioGroup onChange={onChange} value={selectedVoucherId}>
      <List className={classes.voucherOptions} items={renderVoucherOptions} />
    </RadioGroup>
  );
}
